import React, { useState } from 'react';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, DownloadIcon, SearchIcon, FilterIcon, MoreHorizontalIcon, SendIcon, RefreshCwIcon, FileTextIcon, InfoIcon, XIcon } from 'lucide-react';
import { PaymentItem, BulkPaymentBatch } from './BulkPayments';
export function BulkPaymentDetail() {
  const {
    t
  } = useTranslation('payments');
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  // Mock batch data
  const batch: BulkPaymentBatch = {
    id: id || '1',
    name: 'December Invoices',
    createdAt: '2023-12-15T09:30:00',
    totalAmount: 12450.75,
    currency: getTenant().currency.code,
    totalItems: 45,
    completedItems: 38,
    status: 'completed',
    collectionRate: 84.4
  };
  // Mock payment items
  const paymentItems: PaymentItem[] = [{
    id: '1',
    customerName: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+1 (555) 123-4567',
    amount: '349.99',
    currency: getTenant().currency.code,
    description: 'Monthly subscription - Premium Plan',
    reference: 'INV-2023-12-001',
    status: 'paid'
  }, {
    id: '2',
    customerName: 'Sarah Johnson',
    email: 'sarah.j@example.com',
    phone: '+1 (555) 987-6543',
    amount: '199.50',
    currency: getTenant().currency.code,
    description: 'Consulting services - December',
    reference: 'INV-2023-12-002',
    status: 'paid'
  }, {
    id: '3',
    customerName: 'Michael Brown',
    email: 'mbrown@example.com',
    phone: '+1 (555) 456-7890',
    amount: '599.00',
    currency: getTenant().currency.code,
    description: 'Website development - Phase 1',
    reference: 'INV-2023-12-003',
    status: 'pending'
  }, {
    id: '4',
    customerName: 'Emily Wilson',
    email: 'e.wilson@example.com',
    phone: '+1 (555) 234-5678',
    amount: '149.99',
    currency: getTenant().currency.code,
    description: 'Monthly subscription - Basic Plan',
    reference: 'INV-2023-12-004',
    status: 'paid'
  }, {
    id: '5',
    customerName: 'David Lee',
    email: 'david.lee@example.com',
    phone: '+1 (555) 876-5432',
    amount: '299.99',
    currency: getTenant().currency.code,
    description: 'Marketing services - December',
    reference: 'INV-2023-12-005',
    status: 'expired'
  }, {
    id: '6',
    customerName: 'Jennifer Garcia',
    email: 'j.garcia@example.com',
    phone: '+1 (555) 345-6789',
    amount: '499.00',
    currency: getTenant().currency.code,
    description: 'Product photography - Package B',
    reference: 'INV-2023-12-006',
    status: 'paid'
  }, {
    id: '7',
    customerName: 'Robert Martinez',
    email: 'r.martinez@example.com',
    phone: '+1 (555) 765-4321',
    amount: '799.50',
    currency: getTenant().currency.code,
    description: 'Equipment rental - December',
    reference: 'INV-2023-12-007',
    status: 'paid'
  }];
  // Filter payments based on search and status filter
  const filteredPayments = paymentItems.filter(item => {
    const matchesSearch = item.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || item.email.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase()) || item.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter ? item.status === statusFilter : true;
    return matchesSearch && matchesStatus;
  });
  // Count payments by status
  const statusCounts = paymentItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-9193" data-mp="1ayanmbghj537">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-9194" data-mp="1f04ks9rh1qym" />
            {t('pages.getStatusBadge.paid')}
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-9195" data-mp="2qf1dbtmv6yaq">
            <ClockIcon size={12} className="mr-1" data-id="element-9196" data-mp="erxv2iybe5jg" />
            {t('components.renderSchedulePreview.pending')}
          </span>;
      case 'expired':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9197" data-mp="5z4dufzb4w2y">
            <XCircleIcon size={12} className="mr-1" data-id="element-9198" data-mp="z88odeviylt" />
            {t('components.CardOnFilePayment.expired')}
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-9199" data-mp="3n7reb5k15v47">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-9200" data-mp="3j6xlbxrqdq4g" />
            {t('components.getStatusBadge.failed')}
          </span>;
      case 'cancelled':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-9201" data-mp="1hf2rkx5fk6ai">
            <XCircleIcon size={12} className="mr-1" data-id="element-9202" data-mp="1cgri4nj1rxst" />
            {t('pages.getStatusBadge.cancelled')}
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500" data-id="element-9203" data-mp="20ajh8bzhc8lr">
            {status}
          </span>;
    }
  };
  // Navigate back to the payment form with bulk tab selected
  const handleNavigateBack = () => {
    navigate('/payment-requests?tab=bulk');
  };
  const pendingCount = paymentItems.filter(p => p.status === 'pending').length;
  return <div className="space-y-8 pb-12" data-id="element-9204" data-mp="2j9xdj0kolvff">
      {/* Back button */}
      <button onClick={handleNavigateBack} className="flex items-center text-[#9F140F] hover:text-[#5C0C08] text-sm font-medium transition-colors" data-id="element-9205" data-mp="2j7b4ewi577v3">
        <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-9206" data-mp="2a74luw7l7hp2" />
        {t('pages.BulkPaymentDetail.back_to_payment_form')}
      </button>

      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4" data-id="element-9207" data-mp="yu2gkxaxuek7">
        <div data-id="element-9208" data-mp="r9rteru17f9q">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-9209" data-mp="r98bvc8amsw8">{batch.name}</h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-9210" data-mp="mbgieds02ypx">
            {t('pages.BulkPaymentDetail.created_on')}{' '}
            {formatDate(batch.createdAt)}
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-9211" data-mp="vbv8rrjtn40a">
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" data-id="element-9212" data-mp="vcxy3v1hwye3">
            <DownloadIcon size={16} className="mr-2" data-id="element-9213" data-mp="3l8kqb72l0h4" />
            {t('pages.BulkPaymentDetail.export')}
          </button>
          <button onClick={() => setShowReminderModal(true)} className="flex items-center px-4 py-2 text-sm font-medium text-white bg-[#E2231A] hover:bg-[#C41B14] rounded-lg transition-colors shadow-sm" data-id="element-9214" data-mp="hcvk4jwb6p44">
            <SendIcon size={16} className="mr-2" data-id="element-9215" data-mp="3kyu8d6wyydah" />
            {t('pages.BulkPaymentDetail.send_reminders')}
          </button>
        </div>
      </div>

      {/* KPIs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-9216" data-mp="2vhv3wpz4h11q">
        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-9217" data-mp="2zh3ujlyv8it2">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-9218" data-mp="34h8b1bzrxdja">
            {t('components.BulkPaymentList.total_amount')}
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-9219" data-mp="39hmm72wo93lj">
            {formatCurrencyAmount(batch.totalAmount, {
            currency: batch.currency,
            fractionDigits: 2
          })}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-9220" data-mp="3iedf56fxg2bw">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-9221" data-mp="3n9jb6y31mxdp">
            {t('pages.BulkPaymentDetail.collection_rate')}
          </div>
          <div className="flex items-center mt-1" data-id="element-9222" data-mp="10rrwbtxwrj2">
            <div className="text-2xl font-bold text-gray-900 mr-3" data-id="element-9223" data-mp="122vobndxza6">
              {batch.collectionRate}%
            </div>
            <div className="w-full max-w-[100px] bg-gray-100 rounded-full h-2" data-id="element-9224" data-mp="1qhdkwf5qcs2z">
              <div className={`h-2 rounded-full ${batch.collectionRate >= 80 ? 'bg-green-500' : batch.collectionRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
              width: `${batch.collectionRate}%`
            }} data-id="element-9225" data-mp="1qfan38yc5unp"></div>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1.5 font-medium" data-id="element-9226" data-mp="297lk4m6b6ydw">
            {batch.completedItems} {t('components.BulkPaymentList.of')}{' '}
            {batch.totalItems} {t('components.BulkPaymentList.paid')}
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-9227" data-mp="1zgqv5a1ohkor">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-9228" data-mp="1uhxitky4nzq6">
            {t('components.BulkPaymentList.status')}
          </div>
          <div className="flex items-center mt-1" data-id="element-9229" data-mp="2rznw3k809qht">
            {batch.status === 'completed' ? <CheckCircleIcon size={20} className="text-green-500 mr-2" data-id="element-9230" data-mp="2n5jkgnnsouae" /> : batch.status === 'processing' ? <ClockIcon size={20} className="text-blue-500 mr-2" data-id="element-9231" data-mp="m40vudjfstua" /> : <AlertCircleIcon size={20} className="text-red-500 mr-2" data-id="element-9232" data-mp="m6dk18fsg0fs" />}
            <span className="text-xl font-bold text-gray-900 capitalize" data-id="element-9233" data-mp="101perfty1qwp">
              {batch.status}
            </span>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5 rounded-xl shadow-sm" data-id="element-9234" data-mp="193oxqdoir52a">
          <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-9235" data-mp="1534xv2ck9ncg">
            {t('pages.BulkPaymentDetail.payment_requests')}
          </div>
          <div className="text-2xl font-bold text-gray-900" data-id="element-9236" data-mp="1itrzb29phrc8">
            {batch.totalItems}
          </div>
          <div className="flex items-center text-xs font-medium text-gray-500 mt-1.5" data-id="element-9237" data-mp="1dvyv2eryr7el">
            <RefreshCwIcon size={12} className="mr-1" data-id="element-9238" data-mp="1st9138lu7l2x" />
            {t('pages.BulkPaymentDetail.last_updated_5_mins_ago')}
          </div>
        </div>
      </div>

      {/* Payment requests list */}
      <div className="pt-8 border-t border-gray-200" data-id="element-9239" data-mp="27ghgymq3cel8">
        <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-9240" data-mp="2bghwkaoxevtv">
          {t('pages.BulkPaymentDetail.payment_requests')}
        </h3>

        {/* Search and filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0" data-id="element-9241" data-mp="1xj3755yoao1w">
          <div className="flex flex-wrap gap-2" data-id="element-9242" data-mp="1nrfh99hrlf2j">
            <button className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === null ? 'bg-[#E2231A]/10 text-[#9F140F]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(null)} data-id="element-9243" data-mp="1npmc5ymsv2wn">
              {t('pages.BulkPaymentDetail.all')}
              {paymentItems.length})
            </button>
            {Object.entries(statusCounts).map(([status, count]) => <button key={status} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${statusFilter === status ? 'bg-[#E2231A]/10 text-[#9F140F]' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} onClick={() => setStatusFilter(status === statusFilter ? null : status)} data-id="element-9244" data-mp="19rxnsgtixdmu">
                {status.charAt(0).toUpperCase() + status.slice(1)} ({count})
              </button>)}
          </div>
          <div className="relative w-full sm:w-72" data-id="element-9245" data-mp="19klvnrpjg01y">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9246" data-mp="14mkghhv8gkvr">
              <SearchIcon size={16} className="text-gray-400" data-id="element-9247" data-mp="znh8ppnvc0k3" />
            </div>
            <input type="search" aria-label={t('pages.BulkPaymentDetail.attrs.search_payments')} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-[#E2231A] focus:border-[#E2231A] text-sm" placeholder={t('pages.BulkPaymentDetail.attrs.search_payments')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-9248" data-mp="zpk9n4qk7e3l" />
          </div>
        </div>

        {/* Payments table */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-9249" data-mp="24uqwui2i2ty9">
          <div className="overflow-x-auto" data-id="element-9250" data-mp="24snzfcz0pyji">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-9251" data-mp="29lqr1fk8ixx2">
              <thead className="bg-gray-50" data-id="element-9252" data-mp="2ekk63cf66wzc">
                <tr data-id="element-9253" data-mp="2ejsai1lkoz92">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9254" data-mp="2ekahnt9rwxvr">
                    {t('components.BulkPaymentPreview.customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9255" data-mp="2nm1nqz8876q5">
                    {t('components.BulkPaymentPreview.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9256" data-mp="t9bqc7inffe0">
                    {t('components.BulkPaymentPreview.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9257" data-mp="2opigpq2y942m">
                    {t('components.BulkPaymentPreview.reference')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9258" data-mp="2xlri3aahmcre">
                    {t('components.BulkPaymentList.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9259" data-mp="25ujz0hj5yizp">
                    {t('components.BulkPaymentList.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-9260" data-mp="29y9jovwpwgfg">
                {filteredPayments.map(payment => <tr key={payment.id} className="hover:bg-gray-50 transition-colors group" data-id="element-9261" data-mp="1m7m7vzjol6nm">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-9262" data-mp="183oqgntwbcjx">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9263" data-mp="1823r8kwmz5fq">
                        {payment.customerName}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-9264" data-mp="y5qzvtokd3o5">
                        {payment.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-9265" data-mp="1327rixuz205z">
                      <div className="text-sm font-medium text-gray-900" data-id="element-9266" data-mp="1qv7r4o0g6eis">
                        {formatCurrencyAmount(parseFloat(payment.amount), {
                      currency: payment.currency,
                      fractionDigits: 2
                    })}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-9267" data-mp="1lwwhada5k40k">
                      <div className="text-sm text-gray-500 max-w-xs truncate" data-id="element-9268" data-mp="5gadpdrjej0d">
                        {payment.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-9269" data-mp="2d2br9s3ei7ia">
                      {payment.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-9270" data-mp="283qrtuvz3ruw">
                      {getStatusBadge(payment.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-9271" data-mp="1ydp4j5bg2dh1">
                      <div className="relative inline-block text-left" data-id="element-9272" data-mp="1tecfqf10iwbt">
                        <button aria-label="Payment actions" className="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-9273" data-mp="1tc841awvgt0z">
                          <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-9274" data-mp="1ff32no83sic2" />
                        </button>
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-10 hidden group-hover:block" data-id="element-9275" data-mp="3erxfe2muqycu">
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9276" data-mp="39qhx2ic275y5">
                            <SendIcon size={16} className="mr-3 text-gray-400" data-id="element-9277" data-mp="3mawwu2qr6ftk" />

                            {t('pages.BulkPaymentDetail.resend')}
                          </button>
                          <button className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9278" data-mp="3w32mv6xg2n64">
                            <FileTextIcon size={16} className="mr-3 text-gray-400" data-id="element-9279" data-mp="33ccbysfgg6od" />

                            {t('pages.BulkPaymentDetail.view_details')}
                          </button>
                          {payment.status === 'pending' && <button className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9280" data-mp="nkemp2rxbuwp">
                              <XCircleIcon size={16} className="mr-3 text-red-500" data-id="element-9281" data-mp="sig65ot9hw8p" />

                              {t('pages.BulkPaymentDetail.cancel_request')}
                            </button>}
                        </div>
                      </div>
                    </td>
                  </tr>)}
                {filteredPayments.length === 0 && <tr data-id="element-9282" data-mp="1wd8e1ruewob0">
                    <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500" data-id="element-9283" data-mp="1wgnuaxzto3e0">
                      {t('pages.BulkPaymentDetail.no_payment_requests_found_matching_your')}
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reminder Confirmation Modal */}
      {showReminderModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-9284" data-mp="2ini7knd125c1">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden" data-id="element-9285" data-mp="2nmuqix0s2s9c">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-9286" data-mp="28xosvdhl9a3k">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9287" data-mp="2dw8fzh7x4rxi">
                {t('pages.BulkPaymentDetail.send_reminders')}
              </h3>
              <button onClick={() => setShowReminderModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-9288" data-mp="1zv5x1dc7zxqt">
                <XIcon size={20} aria-hidden="true" data-id="element-9289" data-mp="1q0npfx3oni1q" />
              </button>
            </div>
            <div className="p-6" data-id="element-9290" data-mp="1uxw7zysqto3i">
              <p className="text-sm text-gray-600" data-id="element-9291" data-mp="3kcu0mvwktyu1">
                {t('pages.BulkPaymentDetail.this_will_send_payment_reminders_to')}{' '}
                <span className="font-semibold text-gray-900" data-id="element-9292" data-mp="3p9b02m987zmf">
                  {pendingCount}
                </span>{' '}
                {t('pages.BulkPaymentDetail.customers_with_pending_payments')}
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end space-x-3" data-id="element-9293" data-mp="nqanwn429345">
              <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-9294" data-mp="so2ixq0055ck">
                {t('components.CardOnFilePayment.cancel')}
              </button>
              <button className="px-4 py-2 text-sm font-medium text-white bg-[#E2231A] hover:bg-[#C41B14] rounded-lg transition-colors shadow-sm" onClick={() => setShowReminderModal(false)} data-id="element-9295" data-mp="16g0l1gvaq0z8">
                {t('pages.BulkPaymentDetail.send_reminders')}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}